// src/components/HowWeWork.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../lib/firebase";
import "./HowWeWork.css";
import useIsMobile from "../hooks/useIsMobile";
import { adjustFontSize } from "../utils/typography";

export default function HowWeWork({ config: configProp, fontStyles }) {
  const [fetchedConfig, setFetchedConfig] = useState(null);
  const [loading, setLoading] = useState(!configProp);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (configProp) {
      setLoading(false);
      return undefined;
    }

    const unsub = onValue(
      ref(db, "homepageconfig/howWeWork"),
      (snap) => {
        if (snap.exists()) {
          setFetchedConfig(snap.val());
        }
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [configProp]);

  const activeConfig = configProp || fetchedConfig;

  // Get section styles based on useSectionColors flag
  const sectionStyles = useMemo(() => {
    if (!activeConfig || !fontStyles) return {};

    const useSectionColors = activeConfig.useSectionColors !== false;
    const sectionStyleOverrides = activeConfig.sectionStyles || {};
    const sizeOverrides =
      activeConfig.useSectionFontSizes === true
        ? sectionStyleOverrides.fontSizes || {}
        : {};

    const headingColor =
      (useSectionColors ? sectionStyleOverrides.headingColor : null) ||
      (useSectionColors ? activeConfig.titleColor : null) ||
      fontStyles?.heading?.color ||
      "#d4af37";

    const subHeadingColor =
      (useSectionColors ? sectionStyleOverrides.subHeadingColor : null) ||
      (useSectionColors ? activeConfig.subtitleColor : null) ||
      fontStyles?.subHeading?.color ||
      "rgba(255, 255, 255, 0.8)";

    const numberColor =
      (useSectionColors ? sectionStyleOverrides.numberColor : null) ||
      (useSectionColors ? activeConfig.numberColor : null) ||
      fontStyles?.stats?.color ||
      "#d4af37";

    const contentColor =
      (useSectionColors ? sectionStyleOverrides.contentColor : null) ||
      (useSectionColors ? activeConfig.textColor : null) ||
      fontStyles?.content?.color ||
      "#111111";

    return {
      sectionTitle: {
        fontFamily: fontStyles?.heading?.fontFamily || "inherit",
        fontSize: adjustFontSize(
          sizeOverrides.heading || fontStyles?.heading?.fontSize || "54px",
          isMobile
        ),
        fontWeight: fontStyles?.heading?.fontWeight || "500",
        lineHeight: fontStyles?.heading?.lineHeight || "1.05",
        color: headingColor,
      },
      sectionSubtitle: {
        fontFamily: fontStyles?.subHeading?.fontFamily || "inherit",
        fontSize: adjustFontSize(
          sizeOverrides.subHeading || fontStyles?.subHeading?.fontSize || "14px",
          isMobile
        ),
        fontWeight: fontStyles?.subHeading?.fontWeight || "400",
        lineHeight: fontStyles?.subHeading?.lineHeight || "1.6",
        color: subHeadingColor,
      },
      stepNumber: {
        fontFamily: fontStyles?.stats?.fontFamily || "inherit",
        fontSize: adjustFontSize(
          sizeOverrides.stats || fontStyles?.stats?.fontSize || "54px",
          isMobile
        ),
        fontWeight: fontStyles?.stats?.fontWeight || "600",
        lineHeight: fontStyles?.stats?.lineHeight || "1",
        color: numberColor,
      },
      stepText: {
        fontFamily: fontStyles?.content?.fontFamily || "inherit",
        fontSize: adjustFontSize(
          sizeOverrides.content || fontStyles?.content?.fontSize || "18px",
          isMobile
        ),
        fontWeight: fontStyles?.content?.fontWeight || "600",
        lineHeight: fontStyles?.content?.lineHeight || "1.25",
        color: contentColor,
      },
      cardBackground:
        (useSectionColors ? sectionStyleOverrides.cardBgColor : null) ||
        activeConfig.cardBackground ||
        "#ffffff",
    };
  }, [activeConfig, fontStyles, isMobile]);

  if (loading) {
    return <div className="how-we-work loading">Loading...</div>;
  }

  if (!activeConfig || !activeConfig.display) {
    return null;
  }

  const steps = activeConfig.steps || [];

  return (
    <section 
      className="how-we-work" 
      style={{ 
        background: activeConfig.backgroundColor || "#111111",
        backgroundImage: `
          radial-gradient(1200px 500px at 50% 0%, rgba(255,255,255,0.08), transparent 60%),
          linear-gradient(180deg, ${activeConfig.backgroundColor || "#111111"} 0%, ${activeConfig.backgroundColor || "#0b0b0b"} 100%)
        `
      }}
    >
      <div className="how-we-work__container">
        <h2 
          className="how-we-work__title" 
          style={sectionStyles.sectionTitle}
        >
          {activeConfig.sectionTitle || "HOW WE WORK"}
        </h2>

        <p 
          className="how-we-work__subtitle" 
          style={sectionStyles.sectionSubtitle}
        >
          {activeConfig.sectionSubtitle || "At Regency Interior Design Company, we believe that the journey of transforming a space should be as enjoyable as the end result. Here's how we bring your vision to life:"}
        </p>

        {steps.length > 0 && (
          <div className="how-we-work__grid">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className="how-we-work__card"
                style={{ 
                  backgroundColor: sectionStyles.cardBackground || "#ffffff",
                  border: `1px solid ${sectionStyles.cardBackground === 'transparent' ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.06)'}`
                }}
              >
                <div 
                  className="how-we-work__number" 
                  style={sectionStyles.stepNumber}
                >
                  {step.no || (index + 1).toString().padStart(2, '0')}
                </div>
                <div 
                  className="how-we-work__text" 
                  style={sectionStyles.stepText}
                >
                  {step.text}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
